/**
 * Shared helper: resolve next upcoming occurrence for courses.
 *
 * For courses (event_type = 'course'), the `start_date` on producer_events
 * is the FIRST occurrence, which may be in the past. This helper queries
 * event_occurrences to find:
 *  - next upcoming occurrence start_at → used for display
 *  - count of future occurrences
 *
 * Courses with zero future occurrences should be excluded from public lists.
 */
import { supabase } from '@/integrations/supabase/client';

export interface CourseOccurrenceInfo {
  count: number;
  nextStart: string;
}

export interface CourseVisibilityRow {
  event_type: string;
  start_date?: string | null;
  is_recurring?: boolean | null;
  occurrences_count?: number | null;
}

/**
 * Fetch next-occurrence info for a set of course IDs.
 * Returns a Map keyed by event_id.
 */
export async function fetchCourseOccurrenceInfo(
  courseIds: string[],
): Promise<Map<string, CourseOccurrenceInfo>> {
  const result = new Map<string, CourseOccurrenceInfo>();
  if (courseIds.length === 0) return result;

  const nowIso = new Date().toISOString();
  const { data: occRows } = await supabase
    .from('event_occurrences')
    .select('event_id, start_at')
    .in('event_id', courseIds)
    .gte('start_at', nowIso)
    .order('start_at', { ascending: true });

  if (!occRows) return result;

  occRows.forEach((r) => {
    const existing = result.get(r.event_id);
    if (existing) {
      existing.count++;
    } else {
      result.set(r.event_id, { count: 1, nextStart: r.start_at });
    }
  });

  return result;
}

/**
 * Resolve discovery visibility/display date for course rows.
 *
 * Recurring courses must have a future occurrence.
 * Non-recurring courses fall back to their own start_date.
 */
export function resolveCourseVisibilityRow<T extends CourseVisibilityRow>(
  row: T,
  info?: CourseOccurrenceInfo,
): T | null {
  if (row.event_type !== 'course') return row;

  if (info) {
    return {
      ...row,
      start_date: info.nextStart,
      occurrences_count: info.count,
    };
  }

  if (row.is_recurring) return null;
  if (!row.start_date) return null;

  return {
    ...row,
    occurrences_count: row.occurrences_count ?? 0,
  };
}
